/**
 * PLM Statistical QC — the numbers behind the charts (prototype v8's
 * `#chartsView`).
 *
 * Pure functions over the lab-wide QC history, so the statistics that decide
 * whether a lab's QC is in control are unit-testable without a browser.
 *
 * METHOD (kept EXACTLY as the prototype / the lab's spreadsheets):
 *   • Error = re-read − original (signed), each read in the lab's terms
 *     ("<x" = x/2, ND / absent / non-quantity = 0, both "<1" = 0).
 *   • Mean-centred POPULATION σ = √mean((error − mean)²)  — ÷n, not ÷(n−1),
 *     matching the prototype (`statsOf`) and the lab's workbook. Warning =
 *     mean ± 2σ, Action = mean ± 3σ.
 *   • "Exclude ND / ND pairs from the limits" (ON by default) removes those
 *     pairs from EVERYTHING — the plot, the tiles, the table, the deviations and
 *     the precision bands — exactly as the prototype's `filtered()` drops them
 *     from `pts`; they are only counted in the Points tile's sub-line. (They
 *     always check at 0/0 and would flatter the σ.) Corrected 2026-09-26: they
 *     used to stay plotted and be dropped from the limit maths only.
 *
 * THE LIMITS ARE DERIVED, NOT STORED — recomputed from whatever window the user
 * is looking at, so a limit stays a statement about how the lab is performing
 * now.
 */
import {
  ASBESTOS_COMPONENT, asbestosTypes, buildQcPairs, classification, epaRange, groupLayers,
  quantity, parseResult,
  type QcLayer,
} from './plmQc';
import type { PlmDailyQcRow, PlmQcHistoryRow } from './plm.api';

/** One plotted point: ONE QC pair, plotted by its total-asbestos error. */
export interface QcPoint {
  /** The re-read layer this came from. */
  sequenceOid: string;
  sampleLayer: string | null;
  batchId: string | null;
  /** 'Intra' | 'Inter'. */
  kind: string;
  /** When the re-read was analysed — the chart's x axis. */
  date: string | null;
  /** Who read the re-read (READ 2). */
  analyst: string | null;
  /** Who read the original (READ 1). */
  analystOriginal: string | null;
  /** The asbestos type(s), or 'ND'. */
  name: string;
  /** The original's total asbestos reading, in the lab's quantitative terms. */
  original: number;
  /** The re-read's total asbestos reading. */
  reRead: number;
  /** The original's asbestos read-back (e.g. 'Chrysotile 8'), for the table. */
  result1: string;
  /** The re-read's asbestos read-back. */
  result2: string;
  /** re-read − original. The value the chart plots. */
  error: number;
  /** READ 1's / READ 2's asbestos type(s) on their own ('ND' when none) — the
   *  data table's Type1 / Type2 and the "type differs" flag (prototype v9). */
  type1: string;
  type2: string;
  /** READ 1 / READ 2 classified the layer as ACM (asbestos over 1%). */
  acm1: boolean;
  acm2: boolean;
  /** READ 1 / READ 2's % as the lab wrote it ("<1", "5", "0"), for the table. */
  pct1: string;
  pct2: string;
  /** Both reads found no asbestos (0 / 0) — a check at zero. Plotted so the
   *  chart is not empty, but excluded from the limit maths by default (the
   *  prototype's "Exclude ND / ND pairs from the limits"). */
  ndPair: boolean;
}

/** Mean / σ of a set of errors, plus the derived warning and action limits. */
export interface QcStats {
  n: number;
  mean: number;
  sd: number;
  /** mean ± 2σ — investigate. */
  warnLo: number; warnHi: number;
  /** mean ± 3σ — action, must be written up. */
  actionLo: number; actionHi: number;
}

/**
 * Turns the lab-wide rows into plottable points — ONE per completed QC pair
 * (prototype v8). See the file header for the error rule.
 */
export function toPoints(rows: readonly PlmQcHistoryRow[]): QcPoint[] {
  const layers = groupLayers(rows as never);
  const meta = new Map(rows.map(r => [r.sequenceOid, r]));
  const out: QcPoint[] = [];

  // A layer's total asbestos %, ND / non-quantity / absent counting as 0.
  const asbestosTotal = (l: QcLayer) => Number(l.components
    .filter(c => c.component === ASBESTOS_COMPONENT)
    .reduce((s, c) => s + (quantity(parseResult(c.result)) ?? 0), 0)
    .toFixed(2));
  // The asbestos read-back a data-table cell shows ("Chrysotile 8", or "ND").
  const summary = (l: QcLayer, total: number) => {
    const types = asbestosTypes(l);
    return types === 'None' ? 'ND' : `${types} ${total}`;
  };
  const typeOf = (l: QcLayer) => (asbestosTypes(l) === 'None' ? 'ND' : asbestosTypes(l));
  // The % as the lab wrote it: one asbestos component → its own text ("<1",
  // "5"); several → their total; none → "0".
  const pctOf = (l: QcLayer, total: number) => {
    const asb = l.components.filter(c => c.component === ASBESTOS_COMPONENT && (c.result ?? '').trim());
    return asb.length === 1 ? String(asb[0]!.result).replace(/%/g, '').trim() : String(total);
  };

  for (const pair of buildQcPairs(layers)) {
    if (!pair.original || pair.state === 'pending') continue;
    // A blind side (the SIMPLE dup flow) has no results to plot.
    if (pair.original.blind || pair.reRead.blind) continue;
    const m = meta.get(pair.reRead.sequenceOid);
    const a = asbestosTotal(pair.original);
    const b = asbestosTotal(pair.reRead);
    const types = asbestosTypes(pair.reRead) !== 'None'
      ? asbestosTypes(pair.reRead)
      : (asbestosTypes(pair.original) !== 'None' ? asbestosTypes(pair.original) : 'ND');
    out.push({
      sequenceOid: pair.reRead.sequenceOid,
      sampleLayer: pair.reRead.sampleLayer,
      batchId: m?.batchId ?? null,
      kind: pair.kind,
      date: m?.analyzedDate ?? null,
      analyst: pair.reRead.analyzedByName,
      analystOriginal: pair.original.analyzedByName,
      name: types,
      original: a,
      reRead: b,
      result1: summary(pair.original, a),
      result2: summary(pair.reRead, b),
      error: Number((b - a).toFixed(2)),
      ndPair: a === 0 && b === 0,
      type1: typeOf(pair.original),
      type2: typeOf(pair.reRead),
      acm1: classification(pair.original) === 'Positive',
      acm2: classification(pair.reRead) === 'Positive',
      pct1: pctOf(pair.original, a),
      pct2: pctOf(pair.reRead, b),
    });
  }
  return out.sort((x, y) => (x.date ?? '').localeCompare(y.date ?? ''));
}

/**
 * The "Standard Reads" points (prototype `records('Std')`): every SUBMITTED
 * Daily QC, READ 1 = the slide's reference (snapshot at Submit), READ 2 = the
 * analyst's read, error = read − reference. A read still blind (not submitted)
 * has no reference to compare with, so it is not plotted.
 */
export function dailyQcPoints(rows: readonly PlmDailyQcRow[]): QcPoint[] {
  return rows
    .filter(r => r.submittedAt && r.refValue != null && r.readValue != null)
    .map(r => {
      const ref = Number(r.refValue), read = Number(r.readValue);
      // A None-Detected slide keeps its raw type since 2026-10-17 (G5 parity): any ND spelling reads "ND".
      const nd = (t: string | null | undefined) => !t || /^\s*(nd|none\s*detected|no\s*asbestos\s*detected)\s*$/i.test(t);
      const t1 = nd(r.refType) ? 'ND' : r.refType!, t2 = nd(r.readTypes) ? 'ND' : r.readTypes!;
      return {
        sequenceOid: r.oid,
        sampleLayer: `${r.referenceId ?? ''} (${r.originalSourceId ?? ''})`,
        batchId: r.dailyQcId,
        kind: 'Std',
        date: r.qcDate,
        analyst: r.analystName,
        analystOriginal: 'Reference',
        name: t2 !== 'ND' ? t2 : t1,
        original: ref,
        reRead: read,
        result1: t1 === 'ND' ? 'ND' : `${t1} ${ref}`,
        result2: t2 === 'ND' ? 'ND' : `${t2} ${read}`,
        error: Number((read - ref).toFixed(2)),
        ndPair: ref === 0 && read === 0,
        type1: t1,
        type2: t2,
        acm1: t1 !== 'ND' && ref > 1,
        acm2: read > 1,
        pct1: String(ref),
        pct2: String(read),
      };
    })
    .sort((x, y) => (x.date ?? '').localeCompare(y.date ?? ''));
}

/** Population σ of a set of numbers (÷n), or null when there is nothing or no
 *  spread. */
function popSd(values: readonly number[]): number | null {
  const n = values.length;
  if (!n) return null;
  const mean = values.reduce((a, b) => a + b, 0) / n;
  const sd = Math.sqrt(values.reduce((a, e) => a + (e - mean) ** 2, 0) / n);
  return Number.isFinite(sd) ? sd : null;
}

/**
 * Mean, σ and the ±2σ / ±3σ limits of a set of points — the prototype's
 * `statsOf`, exactly: POPULATION σ (÷n), returned for ANY non-empty set. A single
 * point, or identical errors, gives σ 0 and limits that collapse onto the mean;
 * the rules then have nothing to apply (`outsideCounts` / `deviations` guard on
 * σ), but the tiles still show the numbers, as the prototype does. Null only
 * when there are no points.
 *
 * Values are RAW — rounding is for display (σ `toFixed(2)`, limits `toFixed(1)`,
 * as the prototype prints them), so a limit is never rounded twice.
 */
export function statsOf(points: readonly QcPoint[]): QcStats | null {
  const n = points.length;
  if (!n) return null;
  const errs = points.map(p => p.error);
  const mean = errs.reduce((a, b) => a + b, 0) / n;
  const sd = popSd(errs) ?? 0;
  return {
    n, mean, sd,
    warnLo: mean - 2 * sd, warnHi: mean + 2 * sd,
    actionLo: mean - 3 * sd, actionHi: mean + 3 * sd,
  };
}

/** The prototype's `niceStep`: round a raw step up to 1 / 2 / 5 / 10 × 10ⁿ. */
export function niceStep(x: number): number {
  const p = Math.pow(10, Math.floor(Math.log10(x || 1)));
  const n = x / p;
  return (n < 1.5 ? 1 : n < 3 ? 2 : n < 7 ? 5 : 10) * p;
}

/**
 * The control chart's Y scale, as the prototype's `drawChart` builds it: the
 * range spans every point AND both action limits, padded 8% each way (1 when it
 * is flat), with gridline ticks every `niceStep(range / 5)`.
 */
export function chartYScale(errors: readonly number[], stats: QcStats | null):
  { domain: [number, number]; ticks: number[] } {
  const ys = [...errors];
  if (stats) ys.push(stats.actionHi, stats.actionLo);
  if (!ys.length) return { domain: [-1, 1], ticks: [-1, 0, 1] };
  let y0 = Math.min(...ys), y1 = Math.max(...ys);
  const pad = (y1 - y0) * 0.08 || 1;
  y0 -= pad; y1 += pad;
  const step = niceStep((y1 - y0) / 5);
  const ticks: number[] = [];
  // Rounded so float drift (0.1 + 0.2) never prints as 0.30000000000000004.
  for (let v = Math.ceil(y0 / step) * step; v <= y1; v += step) ticks.push(Number(v.toFixed(10)));
  return { domain: [y0, y1], ticks };
}

/**
 * The 1-based point numbers the X axis labels with a date — at most six, evenly
 * spread from the first point to the last (prototype `drawChart`).
 */
export function chartXTicks(n: number): number[] {
  const nt = Math.min(6, n);
  const out: number[] = [];
  for (let k = 0; k < nt; k++) out.push(Math.round(k * (n - 1) / Math.max(1, nt - 1)) + 1);
  return [...new Set(out)];
}

/** One horizontal line on the control chart, with its right-hand label. */
export interface QcLimitLine {
  kind: 'action' | 'warning' | 'mean';
  y: number;
  /** The prototype's label: `${name} ${v>0?'+':''}${v.toFixed(1)}`. */
  label: string;
}

/**
 * The lines the control chart draws, top to bottom — Action, Warning, Mean,
 * Warning, Action (prototype `drawChart`).
 *
 * ONE deliberate difference: when σ is 0 (a single point, or identical errors)
 * all five coincide at the mean and their labels print on top of each other —
 * "Waiimg0.60.6", unreadable, in the prototype too (owner screenshot
 * 2026-09-26). With no spread there is no warning or action band to show, so
 * only the Mean is drawn; the tiles still state the collapsed limits.
 */
export function chartLimitLines(stats: QcStats | null): QcLimitLine[] {
  if (!stats) return [];
  const f1 = (v: number) => `${v > 0 ? '+' : ''}${v.toFixed(1)}`;
  const mean: QcLimitLine = { kind: 'mean', y: stats.mean, label: `Mean ${f1(stats.mean)}` };
  if (!stats.sd) return [mean];
  return [
    { kind: 'action',  y: stats.actionHi, label: `Action ${f1(stats.actionHi)}` },
    { kind: 'warning', y: stats.warnHi,   label: `Warning ${f1(stats.warnHi)}` },
    mean,
    { kind: 'warning', y: stats.warnLo,   label: `Warning ${f1(stats.warnLo)}` },
    { kind: 'action',  y: stats.actionLo, label: `Action ${f1(stats.actionLo)}` },
  ];
}

/** How many points sit outside the warning (>2σ) and action (>3σ) limits. The
 *  warning count is CUMULATIVE (it includes the action points), matching the
 *  prototype's "OUTSIDE LIMITS 7 / 7" tile. */
export function outsideCounts(
  points: readonly QcPoint[], stats: QcStats | null,
): { warning: number; action: number } {
  if (!stats || !stats.sd) return { warning: 0, action: 0 };
  const warning = points.filter(p => Math.abs(p.error - stats.mean) > 2 * stats.sd).length;
  const action = points.filter(p => Math.abs(p.error - stats.mean) > 3 * stats.sd).length;
  return { warning, action };
}

/** Where a QC pair's error sits against the lab's own limits (prototype v18
 *  QC Result View): the line under an expanded pair. */
export interface QcPairLimit {
  tone: 'ok' | 'warn' | 'act';
  text: string;
}

/** How many months back the pair-limit line's reference set reaches (the prototype's "rolling 3 months"). */
export const PAIR_LIMIT_MONTHS = 3;

/**
 * The prototype's lab-limit line for one pair: its error against mean ± 2σ /
 * ± 3σ of the lab's other re-reads of the SAME kind (`history` — the caller
 * passes the rolling 3 months). ND / ND pairs are left out of the limits, as on
 * the chart by default. Null when there is not enough history for a spread.
 */
export function pairLimit(point: QcPoint, history: readonly QcPoint[]): QcPairLimit | null {
  const ref = history.filter(p => p.kind === point.kind && !p.ndPair && p.sequenceOid !== point.sequenceOid);
  const stats = statsOf(ref);
  if (!stats || stats.n < 2 || !stats.sd) return null;
  const off = Math.abs(point.error - stats.mean);
  const tone: QcPairLimit['tone'] = off > 3 * stats.sd ? 'act' : off > 2 * stats.sd ? 'warn' : 'ok';
  const where = tone === 'act' ? 'outside ACTION limit'
    : tone === 'warn' ? 'outside warning limit' : 'within lab control limits';
  const f1 = (v: number) => v.toFixed(1);
  return {
    tone,
    text: `${point.name} error ${point.error > 0 ? '+' : ''}${point.error}% — ${where} `
      + `(${point.kind}-dup limits ${f1(stats.mean)} ± ${f1(2 * stats.sd)} / ± ${f1(3 * stats.sd)}, `
      + `rolling ${PAIR_LIMIT_MONTHS} months)`,
  };
}

/** A rule a point or run of points broke. */
export interface QcDeviation {
  /** 'action' must be investigated and written up; 'warn' is a heads-up. */
  severity: 'action' | 'warn';
  /** 1-based position in the plotted series. */
  index: number;
  message: string;
  point: QcPoint;
}

/**
 * The control-chart rules, as the prototype applies them:
 *   • any point outside ±3σ — ACTION;
 *   • two successive points outside ±2σ — warning;
 *   • (reference reads only) eight successive points one side of the mean — bias.
 */
export function deviations(
  points: readonly QcPoint[], stats: QcStats | null, isRef = false,
): QcDeviation[] {
  const out: QcDeviation[] = [];
  if (!stats || !stats.sd) return out;

  points.forEach((p, i) => {
    if (Math.abs(p.error - stats.mean) > 3 * stats.sd) {
      out.push({
        severity: 'action', index: i + 1, point: p,
        message: `Point ${i + 1} outside the ACTION limit (error ${p.error > 0 ? '+' : ''}${p.error})`,
      });
    }
  });

  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!, b = points[i]!;
    if (Math.abs(a.error - stats.mean) > 2 * stats.sd
      && Math.abs(b.error - stats.mean) > 2 * stats.sd) {
      out.push({
        severity: 'warn', index: i + 1, point: b,
        message: `Points ${i}–${i + 1}: two successive points outside the warning limit`,
      });
    }
  }

  if (isRef) {
    let run = 0, side = 0;
    points.forEach((p, i) => {
      const s = Math.sign(p.error - stats.mean);
      if (s !== 0 && s === side) run++; else { side = s; run = s === 0 ? 0 : 1; }
      if (run === 8) {
        out.push({
          severity: 'warn', index: i + 1, point: p,
          message: `Points ${i - 6}–${i + 1}: more than 7 successive points on the same side of the mean (bias)`,
        });
      }
    });
  }

  return out;
}

/** Agreement in two concentration ranges — the prototype's split at 15%, low
 *  and high, because one σ over the whole 0–100% range hides low-range problems.
 *  A point's band is decided by the LARGER of its two reads. */
export interface QcRangeBand {
  label: string;
  /** Points in this band. */
  n: number;
  /** Population σ of the band's errors, or null when the band is empty. */
  sd: number | null;
  /** ± 2σ warning half-width, or null. */
  warning: number | null;
}

export function precisionByRange(points: readonly QcPoint[]): QcRangeBand[] {
  const band = (lo: number, hiExcl: number, label: string): QcRangeBand => {
    const inBand = points.filter(p => {
      const m = Math.max(p.original, p.reRead);
      return m >= lo && m < hiExcl;
    });
    const sd = popSd(inBand.map(p => p.error));
    return {
      label, n: inBand.length,
      sd: sd == null ? null : Number(sd.toFixed(2)),
      warning: sd == null ? null : Number((2 * sd).toFixed(1)),
    };
  };
  return [band(0, 15, '0 – <15%'), band(15, Infinity, '15 – 100%')];
}

/** Disagreements that are not about a number. */
export interface QcQualitativeRow {
  analyst: string;
  /** QC pairs this analyst was involved in (either read). */
  pairs: number;
  /** Pairs where the two reads disagreed on positive/negative. */
  classificationErrors: number;
  /** Pairs where they named different asbestos types. */
  typeErrors: number;
}

/**
 * Per-analyst counts of the errors a control chart cannot show. Each pair is
 * tallied for BOTH analysts (the original reader and the re-read reader), as the
 * prototype does — for an Inter re-read that is two different people, for an
 * Intra re-read the same person once.
 *
 * `onlyReReads` limits the tally to the pairs on screen — the prototype's
 * `qualRows(all, type)` counts the SELECTED type / period / analyst window
 * (ND/ND pairs included), not the whole lab. Pass the window's re-read
 * sequence Oids; omit it for every pair.
 */
export function qualitativeErrors(
  rows: readonly PlmQcHistoryRow[], onlyReReads?: ReadonlySet<string>,
): QcQualitativeRow[] {
  const pairs = buildQcPairs(groupLayers(rows as never))
    .filter(p => p.original && p.state !== 'pending')
    .filter(p => !onlyReReads || onlyReReads.has(p.reRead.sequenceOid));

  const byAnalyst = new Map<string, QcQualitativeRow>();
  const bump = (name: string | null | undefined, classFlip: boolean, typeFlip: boolean) => {
    if (!name) return;
    const row = byAnalyst.get(name)
      ?? { analyst: name, pairs: 0, classificationErrors: 0, typeErrors: 0 };
    row.pairs++;
    if (classFlip) row.classificationErrors++;
    if (typeFlip) row.typeErrors++;
    byAnalyst.set(name, row);
  };
  for (const p of pairs) {
    const classFlip = !!p.checks[0] && !p.checks[0].ok;
    const typeFlip = !!p.checks[1] && !p.checks[1].ok;
    bump(p.reRead.analyzedByName, classFlip, typeFlip);
    if (p.original!.analyzedByName !== p.reRead.analyzedByName) {
      bump(p.original!.analyzedByName, classFlip, typeFlip);
    }
  }
  return [...byAnalyst.values()].sort((a, b) => a.analyst.localeCompare(b.analyst));
}

/** The distinct analysts in a set of rows, for the chart's filter. */
export function analystsOf(rows: readonly PlmQcHistoryRow[]): string[] {
  return [...new Set(rows.map(r => r.analyzedByName).filter((n): n is string => !!n))].sort();
}

/** Reads a stored result the same way the QC tab does. Re-exported so the
 *  charts and the tab can never disagree about what a reading means. */
export { parseResult };

// ── v9: the BTLIMS "PLM Quality Control" plots (prototype v13 `flagsOf` etc.) ──

/** The two cut-offs that split a pair into concentration Categories 1 / 2 / 3,
 *  on the MEAN of the two reads: ≤ 1% · 1 – <15% · ≥ 15%. The prototype marks
 *  these as an open item ("confirm the BTLIMS cut-offs"), so they live here,
 *  in one place, to change when the lab confirms. */
export const QC_CATEGORY_CUTS = [1, 15] as const;

/** The per-pair BTLIMS columns and flags. */
export interface QcFlags {
  /** READ 1 / READ 2, in the lab's quantitative terms ("<1" = 0.5). */
  r1: number;
  r2: number;
  /** (r1 + r2) / 2 — or, for a reference read, the reference value itself. */
  mean: number;
  /** r1 − r2, 2 dp. */
  d: number;
  acm1: 'Y' | 'N';
  acm2: 'Y' | 'N';
  /** 'F' when the two reads name different asbestos types. */
  flagType: 'F' | '';
  /** 'F' when one read is ACM and the other is not. */
  flagAcm: 'F' | '';
  category: 1 | 2 | 3;
  /** Relative difference, (r1 − r2) / mean × 100, 1 dp; null when the mean is 0. */
  rel: number | null;
}

/** The prototype's `flagsOf`, exactly. For a reference read (`isRef`) READ 1 is
 *  the reference, so the pair's "mean" is the reference value. */
export function qcFlags(p: QcPoint, isRef = false): QcFlags {
  const r1 = p.original;
  const r2 = p.reRead;
  const mean = isRef ? r1 : (r1 + r2) / 2;
  const d = Number((r1 - r2).toFixed(2));
  const acm1 = p.acm1 ? 'Y' : 'N';
  const acm2 = p.acm2 ? 'Y' : 'N';
  const [c1, c2] = QC_CATEGORY_CUTS;
  return {
    r1, r2, mean: Number(mean.toFixed(2)), d, acm1, acm2,
    flagType: p.type1 !== p.type2 ? 'F' : '',
    flagAcm: acm1 !== acm2 ? 'F' : '',
    category: mean <= c1 ? 1 : mean < c2 ? 2 : 3,
    rel: mean ? Number((d / mean * 100).toFixed(1)) : null,
  };
}

/**
 * The small-multiple panels: ONE per analyst that has data, busiest first — not
 * BTLIMS's fixed 2×2 grid. A pair belongs to the analyst whose work is being
 * checked (READ 1's); a reference read to its reader. With an analyst picked,
 * there is one panel.
 */
export function plotGroups(
  points: readonly QcPoint[], analyst: string, isRef = false,
): Array<[string, QcPoint[]]> {
  if (analyst !== 'all') return points.length ? [[analyst, [...points]]] : [];
  const by = new Map<string, QcPoint[]>();
  for (const p of points) {
    const k = (isRef ? p.analyst : p.analystOriginal) ?? '—';
    if (!by.has(k)) by.set(k, []);
    by.get(k)!.push(p);
  }
  return [...by.entries()].sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]));
}

/**
 * Youden plot: is READ 2 outside READ 1's EPA 600 acceptable range? Two reads
 * both below 1%, or both zero, are never "outside" (prototype `youdenSvg`).
 */
export function youdenOutside(p: QcPoint): boolean {
  const f = qcFlags(p);
  if ((f.r1 === 0 && f.r2 === 0) || (f.r1 < 1 && f.r2 < 1)) return false;
  const { lo, hi } = epaRange(Math.max(f.r1, 0.5));
  return f.r2 < lo || f.r2 > hi;
}

export type DiffPlot = 'diff' | 'rel';

/** One point on a difference plot, coloured by its distance from the mean. */
export interface DiffRow { point: QcPoint; flags: QcFlags; x: number; y: number; sev: 'ok' | 'warn' | 'act' }

/**
 * Difference vs concentration ((r1+r2)/2 : r1−r2), Relative difference
 * ((r1+r2)/2 : rel.diff), or — for reference reads — Reference error
 * (reference : read − reference). Mean and POPULATION σ of the plotted values,
 * with each point flagged outside 2σ / 3σ (prototype `diffSvg`). A relative
 * difference is undefined at a mean of 0, so those pairs are left out.
 */
export function diffSeries(
  points: readonly QcPoint[], plot: DiffPlot, isRef = false,
): { rows: DiffRow[]; mean: number; sd: number } | null {
  const base = points.map(p => {
    const f = qcFlags(p, isRef);
    const y = isRef ? Number((f.r2 - f.r1).toFixed(2)) : plot === 'rel' ? f.rel : f.d;
    return { point: p, flags: f, x: f.mean, y };
  }).filter((r): r is { point: QcPoint; flags: QcFlags; x: number; y: number } => r.y != null);
  if (!base.length) return null;
  const ys = base.map(r => r.y);
  const mean = ys.reduce((a, b) => a + b, 0) / ys.length;
  const sd = Math.sqrt(ys.reduce((a, b) => a + (b - mean) ** 2, 0) / ys.length);
  const rows = base.map(r => {
    const z = Math.abs(r.y - mean);
    return { ...r, sev: (z > 3 * sd ? 'act' : z > 2 * sd ? 'warn' : 'ok') as DiffRow['sev'] };
  });
  return { rows, mean, sd };
}
